// --- Token storage --------------------------------------------------------
//
// The frontend (chat-website-uai123.web.app / learnandmasterai.com) and the
// backend (chat-website-uai.uc.r.appspot.com) are different registrable
// domains. A cookie set by the backend's response is stored under the
// backend's own origin and is NEVER visible to document.cookie on the
// frontend's origin, in any browser, in any mode - so reading cookies here
// to build a Bearer header never worked. And in incognito mode, third-party
// cookies are blocked outright, so even the primary HttpOnly cookie set by
// /signin often isn't stored at all, leaving no auth whatsoever.
//
// The fix: POST /api/signin now also returns the JWT in its JSON body. We
// store that token here and send it back ourselves as an Authorization
// header, independent of cookies.
//
// sessionStorage (not localStorage) is deliberate: this product runs on
// shared school/lab computers, and sessionStorage is cleared automatically
// when the tab closes, so the next student to sit down doesn't inherit the
// previous student's session. The tradeoff is that a token doesn't survive
// tab close/reopen (a fresh sign-in is required each time) - acceptable for
// this shared-device environment.
const TOKEN_KEY = "access_token";

export function setToken(token) {
  if (token) {
    sessionStorage.setItem(TOKEN_KEY, token);
  }
}

export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

export function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export class ApiError extends Error {
  constructor(status, detail) {
    super(detail || `Request failed with status ${status}`);
    this.status = status;
    this.detail = detail;
  }
}

// fetch() wrapper: always sends cookies + the Bearer fallback, and aborts
// after `timeoutMs` so a stalled backend call can't hang the UI forever.
// An external `signal` can be passed in (e.g. a user-triggered "Stop") and
// will abort the request the same way the timeout does.
export async function authFetch(url, options = {}, { timeoutMs = 30000, signal } = {}) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  if (signal) {
    if (signal.aborted) controller.abort();
    else signal.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    return await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...authHeaders(), ...(options.headers || {}) },
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

// Call with the response from authFetch(). Redirects to /signin on 401,
// throws ApiError (carrying the backend's `detail`) on any other non-2xx so
// callers can surface a real message instead of silently dropping the error.
export async function handleApiResponse(res, navigate) {
  if (res.status === 401) {
    if (navigate) navigate('/signin');
    else window.location.href = '/signin';
    return null;
  }
  if (!res.ok) {
    let detail;
    try {
      const data = await res.json();
      detail = data.detail;
    } catch (e) {
      // response body wasn't JSON; fall back to the generic ApiError message
    }
    throw new ApiError(res.status, detail);
  }
  return res.json();
}

// Turns a caught error (ApiError, AbortError, a plain Error with a specific
// message, or an opaque network failure) into one user-facing string, so
// every call site can show something instead of silently dropping the
// loading indicator.
export function describeApiError(error) {
  if (error instanceof ApiError) {
    // A 403 (as opposed to a 401, which handleApiResponse already redirects
    // on) means the request reached the server as an authenticated user who
    // simply isn't allowed to do this — e.g. a TEACHER hitting an
    // endpoint/action scoped to SCHOOL_ADMIN or MASTER_ADMIN. That's a
    // real, expected outcome for a role-scoped UI (not every gate can be
    // hidden client-side before the backend finishes rolling out), so it
    // must never resolve to a silent no-op. Prefer the backend's own
    // `detail` when it sent one; fall back to a specific permission message
    // instead of the generic "didn't go through" one below, which reads as
    // a transient failure worth retrying when it isn't.
    if (error.status === 403 && !error.detail) {
      return "You don't have permission to do that.";
    }
    // error.detail is the backend's own message when it sent one as JSON.
    // When it didn't (an unhandled 500, a non-JSON error body), we don't
    // know *why* it failed, so we don't guess - no status code, no invented
    // diagnosis. Just what the user needs: it didn't work, nothing was
    // saved, here's what to do next. Callers already console.error the real
    // error object, so this string is presentation-only, not the debugging
    // trail.
    return (
      error.detail ||
      "That didn't go through, and nothing was saved. Please try again, and contact support if it keeps happening."
    );
  }
  if (error && error.name === 'AbortError') {
    return 'Request timed out. Please try again.';
  }
  if (error instanceof TypeError || !error?.message) {
    // TypeError is what fetch() itself throws on a network failure (DNS,
    // CORS, connection refused) - its message isn't useful to show as-is.
    return 'Network error. Please check your connection and try again.';
  }
  return error.message;
}
